import React from 'react';

/**
 * Kit checkbox — token-styled native input with an optional label + hint line.
 * RTL-safe (gap-based row, logical order). Touch-safe label hit area.
 *
 * <Checkbox checked={x} onChange={...} label={t('...')} hint={t('...')} />
 * Without label it renders just the styled input (for table rows etc.).
 * boxEnd renders the box at the row END with the text block leading
 * (justify-between) — the settings "toggle row" pattern (title + description
 * left, checkbox right).
 */
const BOX =
  'h-4 w-4 shrink-0 rounded border-edge-strong text-accent bg-surface ' +
  'focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:opacity-50 cursor-pointer';

const Checkbox = React.forwardRef(function Checkbox(
  { label, hint, boxEnd = false, className = '', labelClassName = '', disabled, ...rest },
  ref
) {
  const input = (
    <input type="checkbox" ref={ref} disabled={disabled} className={`${BOX} ${className}`} {...rest} />
  );
  if (!label) return input;
  const cursor = disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer';
  const text = (
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-sm text-content-secondary">{label}</span>
      {hint && <span className="text-xs text-content-muted">{hint}</span>}
    </span>
  );
  if (boxEnd) {
    return (
      <label
        className={`flex items-center justify-between gap-3 min-h-[40px] select-none ${cursor} ${labelClassName}`}
      >
        {text}
        {input}
      </label>
    );
  }
  return (
    <label
      className={`inline-flex ${hint ? 'items-start' : 'items-center'} gap-2 min-h-[40px] select-none ${cursor} ${labelClassName}`}
    >
      {hint ? <span className="pt-0.5">{input}</span> : input}
      {text}
    </label>
  );
});

export default Checkbox;
